/* Doorway-card crossfade — the feature items rotate through different designs'
 * set/box mockups. Same manners as the collection tiles: the next image is
 * fully loaded before any fade begins, offscreen cards do not animate,
 * reduced-motion turns it off entirely, and the theme editor holds still so the
 * merchant can click what they see. Self-contained: delete this file and the
 * data-feature-fade attribute and nothing else in the theme changes.
 *
 * ONE CLOCK PER ROW. Every card used to run its own timer. Two things went
 * wrong with that (Justin, 2026-08-16: "it's jerky across the thing, the timing
 * isn't good"):
 *
 *   1. the wave offset came from the card's index among the ANIMATING cards,
 *      not its position on screen. Once hand-picked products were interleaved,
 *      Custom Puzzle sat in slot 7 but rippled as if it were 5th, so the wave
 *      jumped over the gaps instead of crossing the row evenly.
 *   2. independent timers drift. Each cycle carries its own decode time, so
 *      after a few minutes the cards had wandered out of formation and the
 *      wave had decayed into cards changing at random.
 *
 * Now each row has a single master clock, and a card's place in the ripple is
 * its real slot in the grid. The formation cannot drift, and the wave crosses
 * what you actually see.
 */
(function () {
  'use strict';
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;

  var medias = document.querySelectorAll('[data-feature-fade]');
  if (!medias.length) return;

  var visible = new WeakSet();
  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) visible.add(e.target); else visible.delete(e.target);
        });
      }, { rootMargin: '80px' })
    : null;

  /* ---- build the cards ------------------------------------------------- */
  var cards = [];
  Array.prototype.forEach.call(medias, function (media) {
    var urls = (media.getAttribute('data-feature-fade') || '').split('|').filter(Boolean);
    var base = media.querySelector('img');
    if (!base || urls.length < 2) return;
    media.classList.add('adg-card__media--fade');

    var layer = document.createElement('img');
    layer.className = 'adg-fade-layer';
    layer.alt = '';
    layer.setAttribute('aria-hidden', 'true');
    media.appendChild(layer);
    if (io) io.observe(media); else visible.add(media);

    // Position in the row as rendered, so the ripple follows the eye. Static
    // cards between two doorways still take up a slot, which is right: the
    // wave should pass over them, not skip the time they occupy.
    var cardEl = media.closest('.adg-card') || media.parentElement;
    var row = cardEl && cardEl.parentElement;
    var slot = row ? Array.prototype.indexOf.call(row.children, cardEl) : 0;

    cards.push({
      media: media, base: base, layer: layer, urls: urls, row: row, slot: slot,
      i: 0, busy: false,
      fadeMs: parseInt(media.getAttribute('data-fade-ms'), 10) || 600,
      pace: media.getAttribute('data-fade-pace') || 'bursts',
      secs: parseFloat(media.getAttribute('data-fade-interval')) || 4
    });
  });
  if (!cards.length) return;

  /* ---- one advance ----------------------------------------------------- */
  function advance(c) {
    if (!c.media.isConnected) return;
    if ((io && !visible.has(c.media)) || c.busy) return;
    c.busy = true;
    c.i = (c.i + 1) % c.urls.length;
    var next = c.urls[c.i];

    var pre = new Image();
    pre.onerror = function () { c.busy = false; };
    pre.onload = function () {
      var done = false;
      function finish() { if (done) return; done = true; c.busy = false; }

      // THE LAYER CARRIES THE OUTGOING FRAME, NOT THE INCOMING ONE.
      // The other way round, the base held the old image underneath for the
      // whole fade. Two landscape shots hide that; a PORTRAIT over a LANDSCAPE
      // does not, so the old one sat sticking out at both sides at full
      // opacity and then blinked away.
      var outgoing = c.base.currentSrc || c.base.src;
      c.layer.style.transition = 'none';
      c.layer.src = outgoing;
      c.layer.classList.add('is-showing');

      requestAnimationFrame(function () { requestAnimationFrame(function () {
        // srcset first: with it still present the browser can re-run selection
        // and override the src we are about to set.
        c.base.removeAttribute('srcset');
        c.base.removeAttribute('sizes');
        c.base.src = next;

        function dissolve() {
          c.layer.style.transition = '';
          requestAnimationFrame(function () { requestAnimationFrame(function () {
            c.layer.classList.remove('is-showing');
            setTimeout(finish, c.fadeMs);
          }); });
        }
        if (c.base.decode) c.base.decode().then(dissolve, dissolve);
        else if (c.base.complete) dissolve();
        else { c.base.onload = dissolve; c.base.onerror = dissolve; }

        // Never let a stalled decode freeze the rotation.
        setTimeout(finish, c.fadeMs + 1500);
      }); });
    };
    pre.src = next;
  }

  /* ---- one clock per row ----------------------------------------------- */
  var rows = [];
  cards.forEach(function (c) {
    var found = null;
    for (var i = 0; i < rows.length; i++) if (rows[i].row === c.row) found = rows[i];
    if (!found) { found = { row: c.row, cards: [] }; rows.push(found); }
    found.cards.push(c);
  });

  rows.forEach(function (group, gi) {
    group.cards.sort(function (a, b) { return a.slot - b.slot; });
    var lead = group.cards[0];
    var gap = Math.round(lead.fadeMs * 0.9);
    var first = lead.slot;
    var span = (group.cards[group.cards.length - 1].slot - first) * gap;

    var rest = lead.pace === 'gentle'
      ? Math.max(lead.fadeMs * 2 + 800, lead.secs * 1000)
      : Math.max(1500, lead.secs * 1000 - 700);
    var quick = 800;

    var round = 0;
    function tick() {
      group.cards.forEach(function (c) {
        setTimeout(function () { advance(c); }, (c.slot - first) * gap);
      });
      round++;
      // bursts keeps its two-quick-then-rest shape; gentle rests every time.
      var wait = (lead.pace === 'bursts' && round % 3 !== 0) ? quick : rest;
      setTimeout(tick, span + wait);
    }
    // Let the page settle, and stagger separate rows so they do not start
    // rippling in lockstep with each other.
    setTimeout(tick, 700 + gi * 500);
  });
})();
